import {SegmentationMaskLoader} from '@wandb/weave/components/Panel2/ImageWithOverlays';
import _ from 'lodash';
import React, {useCallback, useMemo, useState} from 'react';
import {PartRefFromObjSchema} from 'src/state/views/types';

import type {PanelObjSchema} from '../../state/views/panel/types';
import {RunSignature} from '../../types/run';
import {segmentationMaskColor, WBFile} from '../../util/media';
import {useLoadFile} from '../../util/requests';
import {AllMaskControls, MaskControl} from '../MediaCard';

export type MaskData = {
  key: string;
  wbFile: WBFile;
  classLabels: {[key: number]: string};
};

type MasksOverlayProps = {
  masksRef: React.RefObject<HTMLDivElement>;
  mediaKey: string;
  maskData: MaskData[];
  maskOptions?: {maskKeys: string[]; showImage?: boolean};
  controls?: AllMaskControls;
  cardSize: {width: number; height: number};
  mediaSize: {width: number; height: number} | null;
  style?: React.CSSProperties;
  runSignature: RunSignature;
  mediaPanelRef: PartRefFromObjSchema<PanelObjSchema>;
};

type SegmentationMaskFromFileProps = {
  mediaKey: string;
  style?: React.CSSProperties;
  classLabels:
    | {
        [key: number]: string;
      }
    | undefined;
  maskKey: string;
  mask: WBFile;
  runSignature: RunSignature;
  mediaSize: {
    width: number;
    height: number;
  };
  cardSize: {
    width: number;
    height: number;
  };
  mediaPanelRef: PartRefFromObjSchema<PanelObjSchema>;
  maskControls?: {
    [classOrAll: string]: MaskControl;
  };
};

/**
 * Renders segmentation mask from WBFile
 */
const SegmentationMaskFromFile: React.FC<SegmentationMaskFromFileProps> =
  React.memo(props => {
    const {
      mediaKey,
      maskControls,
      mask,
      maskKey,
      runSignature,
      cardSize,
      mediaSize,
      style,
      classLabels,
    } = props;

    const [directURL, setDirectURL] = useState<string | undefined>(undefined);
    const onSuccess = useCallback(
      (__, metadata) => setDirectURL(metadata.directUrl),
      []
    );

    const loaderStyle = useMemo(
      () => ({width: '100%', height: '100%', ...style}),
      [style]
    );

    const classOverlay = useMemo(() => maskControls ?? {}, [maskControls]);

    const classState = useMemo(
      () =>
        Object.fromEntries(
          _.map(classLabels, (name, classID) => {
            const maskControl = maskControls?.[Number(classID)];
            const maskControlRGB = maskControl && maskControl.rgb;
            const [r, g, b] =
              maskControlRGB ?? segmentationMaskColor(parseInt(classID, 10));
            segmentationMaskColor(parseInt(classID, 10));
            const color = `rgb(${r}, ${g}, ${b})`;
            return [classID, {name, color}];
          })
        ),
      [classLabels, maskControls]
    );

    useLoadFile(runSignature, mask.path, {
      onSuccess,
      responseType: 'blob',
    });

    if (directURL == null) {
      return <div />;
    }

    return (
      <SegmentationMaskLoader
        key={maskKey}
        style={loaderStyle}
        mediaSize={mediaSize}
        classOverlay={classOverlay}
        classState={classState}
        directUrl={directURL}
      />
    );
  });

export const MasksOverlay: React.FC<MasksOverlayProps> = React.memo(
  ({
    masksRef,
    mediaKey,
    maskData,
    maskOptions,
    controls,
    cardSize,
    mediaSize,
    style,
    runSignature,
    mediaPanelRef,
  }) => {
    if (maskData.length === 0 || mediaSize == null) {
      return null;
    }

    return (
      <div
        ref={masksRef}
        className="segmentation-mask__container"
        style={style}
        data-test-mask-controls={JSON.stringify(controls?.toggles?.[mediaKey])}>
        {maskData.map(({key, wbFile, classLabels}) => {
          if (!_.includes(maskOptions?.maskKeys ?? [], key)) {
            return null;
          }
          const maskControls = controls?.toggles?.[mediaKey]?.[key];

          return (
            <SegmentationMaskFromFile
              key={key}
              mediaKey={mediaKey}
              style={{position: 'absolute', top: 0}}
              maskControls={maskControls}
              maskKey={key}
              classLabels={classLabels}
              runSignature={runSignature}
              mask={wbFile}
              cardSize={cardSize}
              mediaSize={mediaSize}
              mediaPanelRef={mediaPanelRef}
            />
          );
        })}
      </div>
    );
  }
);

MasksOverlay.displayName = 'MasksOverlay';

export default MasksOverlay;
